import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { ENDPOINTS, authFetch } from "../../api/api";
import "./Dashboard.css";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts]   = useState({});
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [projects, services, team, testimonials, msgs] = await Promise.all([
          authFetch(ENDPOINTS.projects),
          authFetch(ENDPOINTS.services),
          authFetch(ENDPOINTS.team_members),
          authFetch(ENDPOINTS.testimonials),
          authFetch(ENDPOINTS.contact_messages),
        ]);
        setCounts({
          projects:     projects.length,
          services:     services.length,
          team:         team.length,
          testimonials: testimonials.length,
          messages:     msgs.length,
        });
        setMessages(msgs.slice(0, 5));
      } catch (err) {
        console.error("Dashboard error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const STATS = [
    { icon: "fa-solid fa-briefcase",   label: "Projects",     key: "projects",     accent: "#0a9b7c", to: "/admin/projects" },
    { icon: "fa-solid fa-layer-group", label: "Services",     key: "services",     accent: "#c3b996", to: "/admin/services" },
    { icon: "fa-solid fa-users",       label: "Team Members", key: "team",         accent: "#60a5fa", to: "/admin/team" },
    { icon: "fa-solid fa-star",        label: "Testimonials", key: "testimonials", accent: "#fbbf24", to: "/admin/testimonials" },
    { icon: "fa-solid fa-envelope",    label: "Messages",     key: "messages",     accent: "#f87171", to: "/admin/messages" },
  ];

  const ACTIONS = [
    { icon: "fa-solid fa-plus",          label: "Add Project",     to: "/admin/projects",     color: "#0a9b7c" },
    { icon: "fa-solid fa-user-plus",     label: "Add Team Member", to: "/admin/team",         color: "#60a5fa" },
    { icon: "fa-solid fa-quote-right",   label: "Add Testimonial", to: "/admin/testimonials", color: "#fbbf24" },
    { icon: "fa-solid fa-pen-to-square", label: "Edit Hero",       to: "/admin/content",      color: "#c3b996" },
    { icon: "fa-solid fa-tag",           label: "Edit Pricing",    to: "/admin/pricing",      color: "#fb923c" },
    { icon: "fa-solid fa-envelope-open", label: "View Messages",   to: "/admin/messages",     color: "#f87171" },
  ];

  return (
    <div className="dash-page">

      {/* Welcome row */}
      <div className="dash-welcome">
        <div>
          <h1 className="dash-greeting">{greeting}, {user?.username || "Admin"} 👋</h1>
          <p className="dash-sub">Here's what's happening on your platform today.</p>
        </div>
        <div className="dash-live-badge">
          <span className="dash-live-dot"></span>
          System Online
        </div>
      </div>

      {/* Stat cards */}
      <div className="dash-stats-grid">
        {STATS.map((s) => (
          <div
            key={s.key}
            className="dash-stat-card"
            onClick={() => navigate(s.to)}
            style={{ cursor: "pointer" }}
          >
            <div className="dash-stat-icon" style={{ background: `${s.accent}22`, border: `1px solid ${s.accent}44` }}>
              <i className={s.icon} style={{ color: s.accent }}></i>
            </div>
            <div className="dash-stat-value">{loading ? "—" : (counts[s.key] ?? 0)}</div>
            <div className="dash-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div className="dash-section-box">
        <h3 className="dash-section-title">Quick Actions</h3>
        <div className="dash-quick-grid">
          {ACTIONS.map((a) => (
            <div
              key={a.label}
              className="dash-quick-card"
              onClick={() => navigate(a.to)}
            >
              <i className={a.icon} style={{ color: a.color, fontSize: "0.95rem" }}></i>
              <span className="dash-quick-label">{a.label}</span>
              <i className="fa-solid fa-chevron-right" style={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.2)" }}></i>
            </div>
          ))}
        </div>
      </div>

      {/* Recent messages */}
      <div className="dash-section-box">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
          <h3 className="dash-section-title" style={{ margin: 0 }}>Recent Messages</h3>
          <Link to="/admin/messages" className="dash-see-all">See all →</Link>
        </div>

        {loading ? (
          <div className="dash-empty">
            <div className="adm-loading-spinner"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="dash-empty">No messages yet.</div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className="dash-msg-row">
              <div className="dash-msg-avatar">{msg.name?.[0]?.toUpperCase() || "?"}</div>
              <div className="dash-msg-body">
                <div className="dash-msg-top">
                  <span className="dash-msg-name">{msg.name}</span>
                  <span className="dash-msg-email">{msg.email}</span>
                </div>
                <p className="dash-msg-preview">
                  {msg.message?.substring(0, 90)}{msg.message?.length > 90 ? "…" : ""}
                </p>
              </div>
              {!msg.is_read && <span className="adm-badge adm-badge--green">New</span>}
            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default Dashboard;
